import React, { useEffect, useState } from "react";
import { Save, Loader2, Trophy, Info } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Input } from "../../components/ui/input";

const inputCls =
  "bg-white/5 border-white/15 text-white placeholder:text-white/35 focus-visible:ring-[#7fd0f5]/40 h-11 rounded-xl";

const AdminLeaderboard = () => {
  const [prizes, setPrizes] = useState(Array(10).fill(""));
  const [poolLabel, setPoolLabel] = useState("Total Prize Pool");
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/leaderboard")
      .then((res) => {
        const p = res.data.prizes || [];
        setPrizes([...p, ...Array(10)].slice(0, 10).map((x) => x || ""));
        setPoolLabel(res.data.pool_label || "Total Prize Pool");
        setLoaded(true);
      })
      .catch(() => {
        toast.error("Failed to load leaderboard prizes");
        setLoaded(true);
      });
  }, []);

  const setPrize = (i, val) => {
    setPrizes((prev) => prev.map((p, idx) => (idx === i ? val : p)));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/leaderboard", { prizes, pool_label: poolLabel });
      toast.success("Leaderboard settings saved");
    } catch (e) {
      toast.error("Failed to save prizes");
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) {
    return (
      <div className="glass-card py-14 flex justify-center">
        <p className="text-[#bcd9f2]">Loading leaderboard config...</p>
      </div>
    );
  }

  return (
    <div className="glass-card p-5 sm:p-8 max-w-2xl">
      <h3 className="font-display font-bold text-white text-[1.2rem] flex items-center gap-2">
        <Trophy className="w-5 h-5 text-[#ffd94a]" />
        Top 10 prizes
      </h3>
      <p className="mt-1.5 text-[#9fc5e8] text-[0.88rem] leading-relaxed">
        Set the prize for each leaderboard rank. These show on the public leaderboard page.
      </p>

      <div className="mt-6">
        <label className="text-[#9fc5e8] text-[0.78rem] font-bold tracking-[0.14em] uppercase">
          Prize pool text
        </label>
        <Input
          value={poolLabel}
          onChange={(e) => setPoolLabel(e.target.value)}
          placeholder="Total Prize Pool"
          data-testid="admin-pool-label-input"
          className={`${inputCls} mt-2`}
        />
        <p className="mt-1.5 text-[#9fc5e8]/70 text-[0.78rem]">
          Custom text shown above the prize pool amount on the leaderboard page.
        </p>
      </div>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        {prizes.map((p, i) => (
          <div key={i} className="flex items-center gap-3">
            <span
              className={`w-11 h-11 rounded-xl flex items-center justify-center font-display font-bold text-[0.95rem] shrink-0 border ${
                i === 0
                  ? "bg-gradient-to-b from-[#ffe887] to-[#e2b422] text-[#6b4a0e] border-white/40"
                  : i === 1
                  ? "bg-gradient-to-b from-[#eef4fb] to-[#a6bcd4] text-[#39475a] border-white/40"
                  : i === 2
                  ? "bg-gradient-to-b from-[#f2c197] to-[#b97a3c] text-[#4a2c14] border-white/40"
                  : "bg-white/8 text-[#9fc5e8] border-white/15"
              }`}
            >
              #{i + 1}
            </span>
            <Input
              value={p}
              onChange={(e) => setPrize(i, e.target.value)}
              placeholder={`Prize for rank ${i + 1}`}
              className={inputCls}
            />
          </div>
        ))}
      </div>

      <div className="mt-6 flex items-start gap-3 rounded-xl border border-white/15 bg-white/5 px-4 py-3.5">
        <Info className="w-4 h-4 text-[#7fd0f5] mt-0.5 shrink-0" />
        <p className="text-[#bcd9f2] text-[0.8rem] leading-relaxed">
          Usernames and wager amounts currently show as “null” placeholders on the leaderboard page until the wager race data feed is connected.
        </p>
      </div>

      <button type="button" onClick={save} disabled={saving} className="btn-pill btn-yellow !py-3 !px-7 text-[0.75rem] mt-6 disabled:opacity-60">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        Save prizes
      </button>
    </div>
  );
};

export default AdminLeaderboard;
